// how-to-start/utils/reveal-queue.ts
//
// WHICH SLIDE THE DOCUMENT SHOWS NEXT.
//
// A deck is generated twenty slides at a time and they come back in any
// order, but it has to APPEAR in order: a document that starts at its second
// slide and fills the cover in later looks broken, whatever it ends up as.
// So the reveal walks the deck from the top -- the next position due goes in
// once it has arrived, and nothing behind it goes in before it.
//
// The two ways that goes wrong are both encoded here:
//
//   - a slide that failed every attempt never arrives, and must not hold the
//     rest of the deck behind it forever;
//   - once generation is over, a position still missing is never coming, so
//     waiting on it would strand every slide after it.
//
// Deciding this in one pure function keeps the "never out of order" promise
// somewhere it can be tested, rather than spread through an async loop.

export type RevealStep =
  /** Put this slide in the document now. */
  | { action: "place"; position: number }
  /** This one is never arriving; move past it. */
  | { action: "skip"; position: number }
  /** Due slide has not arrived yet -- hold, and show nothing after it. */
  | { action: "wait" }
  /** Nothing left to reveal. */
  | { action: "done" };

export const nextRevealStep = ({
  positions,
  cursor,
  arrived,
  failed,
  generating,
}: {
  /** Every position this run is responsible for, ascending. */
  positions: readonly number[];
  /** How far down `positions` the reveal has got. */
  cursor: number;
  /** Positions generated and waiting to be shown. */
  arrived: ReadonlySet<number>;
  /** Positions that failed every attempt. */
  failed: ReadonlySet<number>;
  /** Whether more slides may still arrive. */
  generating: boolean;
}): RevealStep => {
  if (cursor >= positions.length) return { action: "done" };
  const due = positions[cursor];
  if (arrived.has(due)) return { action: "place", position: due };
  if (failed.has(due)) return { action: "skip", position: due };
  // Generation is finished and this one never came, so nothing is gained by
  // holding the slides behind it.
  if (!generating) return { action: "skip", position: due };
  return { action: "wait" };
};
